import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { bool, func, array, object, number } from 'prop-types';
import { EmailSent, FileDownload } from 'library/icons';
import { Header4 } from 'library/components/text-elements/headings';
import { BodyText } from 'library/components/text-elements/text';
import { withUser } from 'util/hoc';
import Query from 'library/util/query';
import {
  generateBulkyReportMutation,
  generateReportMutation,
} from 'apps/journey/pages/professional-center/queries/generateReportMutation';
import { pluralize } from 'library/helpers/text';
import GenerateReportModalBody from './generate-report-modal-body';
import ProfCenterModal from '../prof-center-modal';

const StyledSuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  > * {
    margin-top: 1.5em;
  }
`;

const StyledGenerateReportBodyWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const GenerateReportModal = ({
  active,
  toggleActive,
  user,
  individuals,
  selectAll,
  totalCount,
  searchQuery,
  filters,
}) => {
  const [loading, setLoading] = useState(false);
  const [successStatus, setSuccessStatus] = useState(false);

  const [basicDataChecked, setBasicDataChecked] = useState([]);
  const [outcomesChecked, setOutcomesChecked] = useState([]);

  const bulkySelect = selectAll && totalCount > 500;
  const selectedCount = bulkySelect ? totalCount : individuals.length;

  const handleChecks = useCallback((checkData, all, inp, setDataCallback) => {
    if (all?.length) {
      if (checkData.length === all.length) {
        setDataCallback([]);
      } else {
        setDataCallback(all);
      }
    } else {
      if (checkData.some((x) => x === inp)) {
        setDataCallback(checkData.filter((x) => x !== inp));
      } else {
        setDataCallback([...checkData, inp]);
      }
    }
  }, []);

  const handleBasicDataChecked = (inp, all) => {
    handleChecks(basicDataChecked, all, inp, setBasicDataChecked);
  };

  const handleOutcomesChecked = (inp, all) => {
    handleChecks(outcomesChecked, all, inp, setOutcomesChecked);
  };

  const handleSubmit = async () => {
    if (outcomesChecked.length) {
      setLoading(true);
      try {
        const bulkySelectData = searchQuery({
          query: '',
          offset: 0,
          limit: totalCount,
          dynamic_user_data: filters,
          bulkyCheckAll: true,
        });
        const queryString = bulkySelect
          ? generateBulkyReportMutation(bulkySelectData, outcomesChecked)
          : generateReportMutation(individuals, outcomesChecked);
        await Query(queryString);
      } catch (e) {
        setSuccessStatus(false);
        setLoading(false);
      } finally {
        setSuccessStatus(true);
        setLoading(false);
      }
    }
  };

  return (
    <ProfCenterModal
      active={active}
      loading={loading}
      handleSubmit={successStatus ? toggleActive : handleSubmit}
      renderCancelButton={!successStatus}
      submitText={successStatus ? 'Close' : 'Generate Report'}
      disableSubmit={!outcomesChecked.length}
      toggleActive={toggleActive}
      modalHeading={successStatus ? '' : 'Generate Report'}
      modalDetailText={
        successStatus
          ? ''
          : `Generate Report for the ${selectedCount} ${pluralize(
              'Individuals',
              selectedCount
            )} selected. Choose what data to include in the report below. A csv report will be emailed to ${
              user.email
            } when generated. Please allow up to 1 hour for the report to fully generate.`
      }
      icon={!successStatus && <FileDownload width="1em" />}
    >
      <StyledGenerateReportBodyWrapper>
        {successStatus ? (
          <StyledSuccessContainer>
            <Header4>Report Sent</Header4>
            <BodyText>
              We are generating your report and will email it to {user.email}{' '}
              when complete. Please allow up to 1 hour for report generation on
              large data sets.
            </BodyText>
            <EmailSent width={200} />
          </StyledSuccessContainer>
        ) : (
          <GenerateReportModalBody
            basicDataChecked={basicDataChecked}
            handleBasicDataChecked={handleBasicDataChecked}
            outcomesChecked={outcomesChecked}
            handleOutcomesChecked={handleOutcomesChecked}
          />
        )}
      </StyledGenerateReportBodyWrapper>
    </ProfCenterModal>
  );
};

GenerateReportModal.propTypes = {
  active: bool,
  toggleActive: func,
  searchQuery: func,
  user: object,
  individuals: array,
  filters: array,
  selectAll: bool,
  totalCount: number,
};

GenerateReportModal.defaultProps = {
  individuals: [],
};

export default withUser(GenerateReportModal);
